import React, { FC, useContext, useEffect, useState } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import {
  BackwardIcon,
  Button,
  H2,
  Link,
  Preloader,
  Toggle,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import {
  NavLink,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import {
  ActiveAndPeriod,
  AddClientModal,
  AddPositionModal,
  AreasAction,
  Clients,
  Generate,
  IFormValues,
  MainInfoCampaign,
  Positions,
} from 'components';
import { LocalizationContext } from 'localization';
import {
  CREATE_PROMO_CAMPAIGN,
  DELETE_PROMO_CAMPAIGN,
  GET_PROMO_CAMPAIGN,
  ICreatePromoCampaignRequest,
  ICreatePromoCampaignResponse,
  IDeletePromoCampaignRequest,
  IDeletePromoCampaignResponse,
  IGetPromoCampaignRequest,
  IGetPromoCampaignResponse,
  IUpdatePromoCampaignRequest,
  IUpdatePromoCampaignResponse,
  TPromoCampaignData,
  UPDATE_PROMO_CAMPAIGN,
} from 'schemas';
import { openConfirmDialog } from 'services/Confirmation';
import notification from 'services/notificationService';
import { SettingsContext } from 'settings';
import { EPromoCodeMode, EPromoScopeType, EPromoValueType } from 'types';

import { DEFAULT_VALUES } from './constants';
import {
  ETabsModal,
  IApplicableParamsParsed,
  IChosenClientItem,
  IChosenPositions,
} from './interfaces';
import s from './PromoCampaignManager.module.scss';
import { normaliseSubmitPromoInput } from './utils';

export const PromoCampaignManager: FC = () => {
  const { t } = useContext(LocalizationContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action');

  const {
    state: {
      settings: { telegramChannelId },
    },
  } = useContext(SettingsContext);

  const [openPositionModal, setOpenPositionModal] = useState<boolean>(false);
  const [openClientModal, setOpenClientModal] = useState<boolean>(false);
  const [initialActiveFrom, setInitialActiveFrom] = useState<string | null>(
    null
  );
  const [initialActiveTo, setInitialActiveTo] = useState<string | null>(null);
  const [chosenPositions, setChosenPositions] = useState<
    Array<IChosenPositions>
  >([]);
  const [chosenItems, setChosenItems] = useState<IChosenClientItem[]>([]);
  const areasPromoCampaign = [
    {
      title: t('settings.tabs.billing'),
      value: EPromoScopeType.BILLING,
      active: false,
      disabled: false,
    },
    {
      title: t('promo.areas.mobile.app'),
      value: EPromoScopeType.MOBILE_APP,
      active: false,
      disabled: false,
    },
    {
      title: 'Shell',
      value: EPromoScopeType.SHELL,
      active: false,
      disabled: false,
    },
  ];

  const [areas, setAreas] = useState(areasPromoCampaign);
  const [initialAreas, setInitialAreas] = useState(areasPromoCampaign);
  const [initialChosenPositions, setInitialChosenPositions] = useState<
    Array<IChosenPositions>
  >([]);
  const [initialChosenItems, setInitialChosenItems] = useState<
    IChosenClientItem[]
  >([]);
  const [isOverallDirty, setIsOverallDirty] = useState(false);

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });

  const resetFormData = (promoCampaignData: TPromoCampaignData): void => {
    reset({
      title: promoCampaignData.title,
      active: promoCampaignData.active,
      rewardType: promoCampaignData.value.type,
      rewardValue: promoCampaignData.value.value,
      is_tracked: promoCampaignData.is_tracked,
      activeTo: promoCampaignData.active_to,
      activeFrom: promoCampaignData.active_from,
      prefix: promoCampaignData.generation_settings.prefix,
      format: promoCampaignData.generation_settings.type,
      codeLength: promoCampaignData.generation_settings.length,
      codesCount: promoCampaignData.counters.total,
    });

    setInitialActiveFrom(promoCampaignData.active_from);
    setInitialActiveTo(promoCampaignData.active_to);

    if (promoCampaignData.scopes) {
      const newAreas = areas.map((area) => ({
        ...area,
        active: promoCampaignData.scopes.includes(area.value),
      }));
      setAreas(newAreas);
      setInitialAreas(newAreas);
    }

    if (promoCampaignData.client_scope) {
      const normalizeClientScope = promoCampaignData.client_scope.map(
        (item): IChosenClientItem => ({
          id: item.id,
          type: item.__typename === 'ScopedClient' ? 'client' : 'group',
          title:
            item.__typename === 'ScopedClient'
              ? (item.phone as string)
              : (item.title as string),
        })
      );

      setChosenItems(normalizeClientScope);
      setInitialChosenItems([...normalizeClientScope]);
    }

    if (promoCampaignData.applicable_params) {
      const data: IApplicableParamsParsed = JSON.parse(
        promoCampaignData.applicable_params
      );

      const normalizeApplicableParams: Array<IChosenPositions> = [];

      for (const type in data) {
        const item = data[type as 'goods' | 'services' | 'tariffs'];
        let normalizeApplicableParamsType: ETabsModal | undefined;
        let normalizeApplicableParamsTitle = '';

        switch (type) {
          case 'goods':
            normalizeApplicableParamsType = ETabsModal.GOODS;
            normalizeApplicableParamsTitle = t('promo.position.all_goods');
            break;
          case 'services':
            normalizeApplicableParamsType = ETabsModal.SERVICES;
            normalizeApplicableParamsTitle = t('promo.position.all_services');
            break;
          case 'tariffs':
            normalizeApplicableParamsType = ETabsModal.TARIFFS;
            normalizeApplicableParamsTitle = t('promo.position.all_tariffs');
            break;
          default:
            normalizeApplicableParamsTitle = t('visitors.types.all');
            break;
        }

        if (item?.all) {
          normalizeApplicableParams.push({
            id: -1,
            type: normalizeApplicableParamsType,
            title: normalizeApplicableParamsTitle,
          });
        } else if (item?.items) {
          item.items.forEach(({ title, id }) =>
            normalizeApplicableParams.push({
              type: normalizeApplicableParamsType,
              id,
              title,
            })
          );
        }
      }

      setChosenPositions(normalizeApplicableParams);
      setInitialChosenPositions([...normalizeApplicableParams]);
    }
  };

  const [createCampaign, { loading: createLoading }] = useMutation<
    ICreatePromoCampaignResponse,
    ICreatePromoCampaignRequest
  >(CREATE_PROMO_CAMPAIGN, {
    onCompleted: (res) => {
      if (!res.createPromoCampaign) return;

      navigate('/promo-codes', {
        state: { activeTab: EPromoCodeMode.CAMPAIGN },
      });
      notification.info({
        t,
        type: 'success',
        title: t('promo_group.notification.create'),
        dismiss: 5,
      });
    },
    onError: (error) => {
      notification.info({
        t,
        type: 'error',
        title: t('errors.error'),
        message: error?.message,
        dismiss: 5,
      });
    },
  });

  const [updatePromoCampaign, { loading: updateLoading }] = useMutation<
    IUpdatePromoCampaignResponse,
    IUpdatePromoCampaignRequest
  >(UPDATE_PROMO_CAMPAIGN, {
    onCompleted: (res) => {
      if (!res.updatePromoCampaign) return;

      resetFormData(res.updatePromoCampaign);
      navigate('/promo-codes', {
        state: { activeTab: EPromoCodeMode.CAMPAIGN },
      });
      notification.info({
        t,
        type: 'success',
        title: t('promo_group.notification.update'),
        dismiss: 5,
      });
    },
    onError: (error) => {
      notification.info({
        t,
        type: 'error',
        title: t('errors.error'),
        message: error?.message,
        dismiss: 5,
      });
    },
  });

  const [deleteCampaign, { loading: deleteLoading }] = useMutation<
    IDeletePromoCampaignResponse,
    IDeletePromoCampaignRequest
  >(DELETE_PROMO_CAMPAIGN, {
    onCompleted: (res) => {
      if (!res.deletePromoCampaign) return;

      navigate('/promo-codes', {
        state: { activeTab: EPromoCodeMode.CAMPAIGN },
      });
      notification.info({
        t,
        type: 'success',
        title: t('promo_group.notification.delete'),
        dismiss: 5,
      });
    },
    onError: (error) => {
      console.error(error);
      notification.info({
        t,
        type: 'error',
        title: t('errors.error'),
        dismiss: 5,
      });
    },
  });

  const [getPromoCampaign, { data: promoCampaignFetchData }] = useLazyQuery<
    IGetPromoCampaignResponse,
    IGetPromoCampaignRequest
  >(GET_PROMO_CAMPAIGN, {
    onCompleted: (res) => {
      if (!res.promoCampaign) return;
      resetFormData(res.promoCampaign);
    },
  });

  const onSubmit = (values: IFormValues): void => {
    const input = normaliseSubmitPromoInput({
      values,
      chosenItems,
      chosenPositions,
      areas,
      rewardType,
    });

    if (id && !action) {
      const { generation_settings, value, ...updateInput } = input;
      updatePromoCampaign({
        variables: {
          id: id,
          input: updateInput,
        },
      });
    } else {
      createCampaign({
        variables: {
          input,
        },
      });
    }
  };

  useEffect(() => {
    if (!id) return;

    getPromoCampaign({ variables: { promoCampaignId: id } });
  }, [id, promoCampaignFetchData]);

  const watchedActiveFrom = watch('activeFrom');
  const watchedActiveTo = watch('activeTo');
  const rewardType = watch('rewardType');

  useEffect(() => {
    setTimeout(() => {
      if (rewardType === EPromoValueType.DISCOUNT) {
        setAreas((prev) =>
          prev.map((area) => ({
            ...area,
            active: area.value === EPromoScopeType.BILLING,
            disabled: area.value !== EPromoScopeType.BILLING,
          }))
        );
      } else {
        setAreas((prev) =>
          prev.map((area) => ({
            ...area,
            disabled: false,
          }))
        );
      }
    }, 0);
  }, [rewardType]);

  useEffect(() => {
    const hasAreasChanged =
      JSON.stringify(areas) !== JSON.stringify(initialAreas);
    const hasPositionsChanged =
      JSON.stringify(chosenPositions) !==
      JSON.stringify(initialChosenPositions);
    const hasItemsChanged =
      JSON.stringify(chosenItems) !== JSON.stringify(initialChosenItems);
    const hasActiveFromChanged = watchedActiveFrom !== initialActiveFrom;
    const hasActiveToChanged = watchedActiveTo !== initialActiveTo;

    const hasFormChanges =
      isDirty ||
      hasAreasChanged ||
      hasPositionsChanged ||
      hasItemsChanged ||
      hasActiveFromChanged ||
      hasActiveToChanged;

    const hasActiveAreas = areas.some((item) => item.active);

    setIsOverallDirty(hasFormChanges && hasActiveAreas);
  }, [
    isDirty,
    areas,
    chosenPositions,
    chosenItems,
    initialAreas,
    initialChosenPositions,
    initialChosenItems,
    initialActiveFrom,
    initialActiveTo,
    watchedActiveFrom,
    watchedActiveTo,
  ]);

  return (
    <Preloader loading={false}>
      {openPositionModal && (
        <AddPositionModal
          onClose={() => setOpenPositionModal(false)}
          chosenPositions={chosenPositions}
          setChosenPositions={setChosenPositions}
        />
      )}

      {openClientModal && (
        <AddClientModal
          onClose={() => setOpenClientModal(false)}
          chosenItems={chosenItems}
          setChosenItems={setChosenItems}
        />
      )}

      <div className={s.container}>
        <div
          className={s.link_back}
          onClick={() =>
            navigate('/promo-codes', {
              state: { activeTab: EPromoCodeMode.CAMPAIGN },
            })
          }
        >
          <BackwardIcon />
          {t('back')}
        </div>
        <div className={s.content}>
          <div className={s.header_block}>
            <H2>
              {id && !action
                ? t('edit.promo_code.group')
                : t('add.promo_code.group')}
            </H2>

            {!id && (
              <Tooltip
                content={
                  <div className={s.tooltip_content}>
                    <span className={s.text}>
                      {t('promo.manager.tooltip.title')}
                    </span>
                    <Link
                      title={t('more')}
                      link={
                        'https://support.smartshell.gg/hc/ru/articles/16510803851021-%D0%9F%D1%80%D0%BE%D0%BC%D0%BE%D0%BA%D0%BE%D0%B4'
                      }
                    />
                  </div>
                }
                contentClassName={s.tooltip}
              />
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className={s.form}>
            <MainInfoCampaign
              control={control}
              setValue={setValue}
              watch={watch}
              totalCodes={
                promoCampaignFetchData?.promoCampaign?.counters?.total
              }
              usedCodes={promoCampaignFetchData?.promoCampaign?.counters?.used}
              promoOrPromoCampaign={'campaign'}
            />

            <Clients
              control={control}
              setValue={setValue}
              watch={watch}
              setOpenClientModal={setOpenClientModal}
              chosenItems={chosenItems}
              setChosenItems={setChosenItems}
            />

            <Positions
              control={control}
              setValue={setValue}
              watch={watch}
              setOpenPositionModal={setOpenPositionModal}
              chosenPositions={chosenPositions}
              setChosenPositions={setChosenPositions}
              rewardType={rewardType}
            />

            <AreasAction
              areas={areas}
              setAreas={setAreas}
              rewardType={rewardType}
            />

            <Generate
              control={control}
              setValue={setValue}
              watch={watch}
              id={id}
              action={action}
            />

            <ActiveAndPeriod
              control={control}
              setValue={setValue}
              activeTo={promoCampaignFetchData?.promoCampaign.active_to || null}
              activeFrom={
                promoCampaignFetchData?.promoCampaign.active_from || null
              }
            />

            <div className={cn(s.frame, !telegramChannelId?.id && s.disable)}>
              <Controller
                render={({ field }) => (
                  <Toggle
                    {...field}
                    iconName="TelegramBellIcon"
                    label={t('promo.telegram.text')}
                    disabled={!telegramChannelId?.id}
                    className={s.toggle}
                  />
                )}
                name="is_tracked"
                control={control}
              />
              {!telegramChannelId?.id && (
                <span className={s.text_link}>
                  {t('telegramModal.info.text_3')}{' '}
                  <NavLink
                    state={{ selectedTab: 'INTEGRATIONS' }}
                    to={'/settings'}
                    className={s.link}
                  >
                    {t('integration')}
                  </NavLink>{' '}
                </span>
              )}
            </div>
            <div className={s.buttons_block}>
              {id && !action && (
                <Button
                  className={s.button}
                  color="red"
                  isLoading={deleteLoading}
                  variant="secondary"
                  onClick={() => {
                    openConfirmDialog(t, 'remove', {
                      title: t('delete.promo_code.group'),
                      message: t(
                        'promo.campaign.delete.text',
                        getValues('title')
                      ),
                      acceptTitle: t('delete'),
                      declineTitle: t('cancel'),
                    }).then(({ status }) => {
                      if (status === 'ACCEPTED') {
                        deleteCampaign({ variables: { id: id } });
                      }
                    });
                  }}
                >
                  {t('delete')}
                </Button>
              )}
              {chosenPositions.length === 0 &&
              rewardType === EPromoValueType.DISCOUNT ? (
                <Tooltip
                  className={cn(s.button, s.button_save)}
                  content={t('promo.button.submit.disabled')}
                  position="top"
                  contentClassName={s.tooltip}
                >
                  <Button
                    className={cn(s.button, s.button_save)}
                    type="submit"
                    isLoading={createLoading || updateLoading}
                    disabled={!isOverallDirty || chosenPositions.length === 0}
                  >
                    {id ? t('save') : t('add')}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  className={cn(s.button, s.button_save)}
                  type="submit"
                  isLoading={createLoading || updateLoading}
                  disabled={!isOverallDirty}
                >
                  {id ? t('save') : t('add')}
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </Preloader>
  );
};
